// src/redux/identity/identitySlice.ts

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import identityApi from "../../services/identity/identityApi";
import type {
  IdentityEligibilityDTO,
  IdentityStartPayload,
  IdentityStartResponseDTO,
  IdentityState,
  IdentityStatusDTO,
} from "../../types/identity/identityTypes";

// Fetch eligibility
export const fetchIdentityEligibility = createAsyncThunk<
  IdentityEligibilityDTO,
  void,
  { rejectValue: string }
>("identity/fetchEligibility", async (_, { rejectWithValue }) => {
  try {
    return await identityApi.fetchEligibility();
  } catch (error: any) {
    const rawDetail = error?.response?.data?.detail;
    const detail = Array.isArray(rawDetail) ? rawDetail.join(" ") : rawDetail;

    return rejectWithValue(
      detail || "Failed to load identity eligibility."
    );
  }
});

// Fetch identity status
export const fetchIdentityStatus = createAsyncThunk<
  IdentityStatusDTO,
  void,
  { rejectValue: string }
>("identity/fetchStatus", async (_, { rejectWithValue }) => {
  try {
    return await identityApi.fetchStatus();
  } catch (error: any) {
    const rawDetail = error?.response?.data?.detail;
    const detail = Array.isArray(rawDetail) ? rawDetail.join(" ") : rawDetail;

    return rejectWithValue(
      detail || "Failed to load identity status."
    );
  }
});

// Start verification
export const startIdentityVerification = createAsyncThunk<
  IdentityStartResponseDTO,
  IdentityStartPayload | undefined,
  { rejectValue: string }
>("identity/startVerification", async (payload, { rejectWithValue }) => {
  try {
    return await identityApi.startVerification(payload);
  } catch (error: any) {
    const rawDetail = error?.response?.data?.detail;
    const missingFields = error?.response?.data?.missing_fields;

    const detail = Array.isArray(rawDetail)
      ? rawDetail.join(" ")
      : rawDetail;

    if (Array.isArray(missingFields) && missingFields.length > 0) {
      return rejectWithValue(
        `${detail || "Profile is incomplete."} Missing: ${missingFields.join(", ")}`
      );
    }

    return rejectWithValue(detail || "Failed to start identity verification.");
  }
});

const initialState: IdentityState = {
  eligibility: null,
  identityStatus: null,
  verificationUrl: null,

  eligibilityStatus: "idle",
  statusStatus: "idle",
  startStatus: "idle",

  error: null,
};

const identitySlice = createSlice({
  name: "identity",
  initialState,
  reducers: {
    clearIdentityError(state) {
      state.error = null;
    },

    clearVerificationUrl(state) {
      state.verificationUrl = null;
    },

    setIdentityStatus(state, action: PayloadAction<IdentityStatusDTO | null>) {
      state.identityStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Eligibility
      .addCase(fetchIdentityEligibility.pending, (state) => {
        state.eligibilityStatus = "loading";
        state.error = null;
      })
      .addCase(fetchIdentityEligibility.fulfilled, (state, action) => {
        state.eligibilityStatus = "succeeded";
        state.eligibility = action.payload;
      })
      .addCase(fetchIdentityEligibility.rejected, (state, action) => {
        state.eligibilityStatus = "failed";
        state.error = action.payload || "Failed to load identity eligibility.";
      })

      // Status
      .addCase(fetchIdentityStatus.pending, (state) => {
        state.statusStatus = "loading";
        state.error = null;
      })
      .addCase(fetchIdentityStatus.fulfilled, (state, action) => {
        state.statusStatus = "succeeded";
        state.identityStatus = action.payload;
      })
      .addCase(fetchIdentityStatus.rejected, (state, action) => {
        state.statusStatus = "failed";
        state.error = action.payload || "Failed to load identity status.";
      })

      // Start
      .addCase(startIdentityVerification.pending, (state) => {
        state.startStatus = "loading";
        state.error = null;
      })
      .addCase(startIdentityVerification.fulfilled, (state, action) => {
        state.startStatus = "succeeded";
        state.verificationUrl = action.payload.verification_url;
      })
      .addCase(startIdentityVerification.rejected, (state, action) => {
        state.startStatus = "failed";
        state.error = action.payload || "Failed to start identity verification.";
      });
  },
});

export const {
  clearIdentityError,
  clearVerificationUrl,
  setIdentityStatus,
} = identitySlice.actions;

export default identitySlice.reducer;